import { Component, ElementRef, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import {
  StepEditorIds,
  StepElements,
  StepResponseScreen,
  StepResponseScreenLayout,
  CampaignProduct,
  CustomStepCategoryEnum,
  buildPathVariables,
  StepEmulatorDevices,
  StepSectionWidths,
  DiscountSetting,
  OfferTypes,
  User,
  WidgetVariable,
} from '../../../_models';
import { FormControl, FormGroup } from '@angular/forms';
import { Subscription } from 'rxjs';
import { StepEmulatorService, StepTemplateService, UserService, WidgetService } from '../../../_services';
import { addDummyValues, enableImagePaste, FontFamilyFormats, FontSizes, initializeEditorStyles } from '../../step-util';
import { trigger, state, style, animate, transition } from '@angular/animations';
import * as tinymce from 'tinymce';
import { FormControlStatus } from '../../../_forms';
import { ActivatedRoute } from '@angular/router';

@Component({
  selector: 'step-response-screen',
  templateUrl: './step-response-screen.component.html',
  styleUrls: ['./step-response-screen.component.scss'],
  animations: [
    trigger('fadeIn', [
      state('void', style({ opacity: 0 })),
      transition(':enter', [animate('500ms ease-in', style({ opacity: 1 }))]),
    ]),
  ],
})
export class StepResponseScreenComponent implements OnInit, OnDestroy, OnChanges {
  @Input() layout: StepResponseScreenLayout;
  @Input() form: FormGroup;
  @Input() enableFormErrors: boolean;
  @Input() theme: string;
  @Input() product: CampaignProduct = {} as CampaignProduct;
  @Input() stepType: CustomStepCategoryEnum;
  @Input() activeDevice: StepEmulatorDevices;
  @Input() discount: DiscountSetting;
  stepResponseScreen: StepResponseScreen;
  stepEditorIds = StepEditorIds;
  stepElements = StepElements;
  private subscription: Subscription;
  private widgetSubscription: Subscription;
  funnelPathVariables: { value: string; text: string }[] = [];
  stepSectionWidths = StepSectionWidths;
  private changes: SimpleChanges;
  offerType: OfferTypes;
  user: User;

  responseTitleEditor = {
    plugins: 'line-height emoticons paste autoresize semibold swatch-colors',
    toolbar:
      'bold semibold italic underline strikethrough | fontsizeselect fontselect lineheightselect | swatch-forecolor swatch-backcolor | ' +
      'alignleft aligncenter alignright | emoticons ',
    fontsize_formats: FontSizes,
    font_formats: FontFamilyFormats,
    paste_as_text: true,
    auto_focus: this.stepEditorIds.ResponseTitle,
    min_height: 110,
    max_height: 200,
    autoresize_bottom_margin: 0,
    setup: (editor: tinymce.Editor) => {
      initializeEditorStyles(editor, '24px', "'Open Sans', Ubuntu, sans-serif", true);

      editor.on('Change KeyUp', () => {
        const content = editor.getContent();
        this.onTitleChange(content);
      });
    },
  };

  responseEditor = {
    plugins: 'line-height emoticons image media link paste autoresize semibold swatch-colors',
    toolbar:
      'bold semibold italic underline strikethrough link | fontsizeselect fontselect lineheightselect | ' +
      'swatch-forecolor swatch-backcolor | alignleft aligncenter alignright | image media | emoticons',
    fontsize_formats: FontSizes,
    font_formats: FontFamilyFormats,
    paste_as_text: true,
    auto_focus: this.stepEditorIds.ResponseTitle,
    min_height: 200,
    max_height: 300,
    autoresize_bottom_margin: 0,
    setup: (editor: tinymce.Editor) => {
      initializeEditorStyles(editor, '22px', "'Open Sans', Ubuntu, sans-serif", true);
      enableImagePaste(editor);

      editor.on('Change KeyUp', () => {
        const content = editor.getContent();
        this.onResponseChange(content);
      });
    },
  };

  constructor(
    protected stepService: StepTemplateService,
    protected stepEmulatorService: StepEmulatorService,
    protected route: ActivatedRoute,
    protected userService: UserService,
    protected widgetService: WidgetService
  ) {
    this.route.params.subscribe((params) => {
      this.offerType = params['offer_type'] ? parseInt(params['offer_type'], 10) : null;
    });
  }

  ngOnInit() {
    this.fetchUser();

    this.loadWidgetVariables();
    this.subscription = this.stepEmulatorService.stepResponseScreen$.subscribe((data) => {
      this.stepResponseScreen = data;
      this.updateData();
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    this.changes = changes;
    this.updateData();
  }

  private updateData() {
    if (this.stepResponseScreen) {
      if (this.changes) {
        if (this.changes.theme && this.changes.theme.currentValue) {
          this.applyTheme();
        }

        if (this.changes.product && this.product) {
          const title = this.form.get('response_data').get('label').value,
            response = this.form.get('response_data').get('content').value;
          this.onTitleChange(title);
          this.onResponseChange(response);
        }
      }

      this.initalizeValues();
    }
  }

  applyTheme() {
    this.stepResponseScreen.detailButton.backgroundColor = this.theme;
    this.stepResponseScreen.continueButton.borderColor = this.theme;
    this.stepResponseScreen.continueButton.fontColor = this.theme;
  }

  initalizeValues() {
    this.stepResponseScreen.titleContent = addDummyValues(
      this.form.get('response_data').get('label').value,
      this.funnelPathVariables,
      this.product,
      this.discount
    );
    this.stepResponseScreen.responseContent = addDummyValues(
      this.form.get('response_data').get('content').value,
      this.funnelPathVariables,
      this.product,
      this.discount
    );
  }

  toggleElement(element: StepElements) {
    this.stepEmulatorService.toggleElement(element);
  }

  isElementVisible(element: StepElements): boolean {
    return this.stepEmulatorService.isElementVisible(element);
  }

  get isSingleStepOffer(): boolean {
    return this.offerType === OfferTypes.SingleStep;
  }

  get isAdmin(): boolean {
    return this.user && this.user.is_staff;
  }

  fetchUser() {
    this.userService.getCurrent().subscribe((user: User) => {
      this.user = user;
    });
  }

  updateStepResponseScreen() {
    this.stepEmulatorService.updateStepResponseScreen(this.stepResponseScreen);
  }

  onTitleChange(content: string) {
    const updatedContent = addDummyValues(content, this.funnelPathVariables, this.product, this.discount);
    this.stepResponseScreen.titleContent = updatedContent;
  }

  onResponseChange(content: string) {
    const updatedContent = addDummyValues(content, this.funnelPathVariables, this.product, this.discount);
    this.stepResponseScreen.responseContent = updatedContent;
  }

  addResponsTitleVariable(event: any) {
    const selectElement = event.target as HTMLSelectElement;
    const selectedValue = selectElement.value;
    const editorInstance = tinymce.get(this.stepEditorIds.ResponseTitle);
    if (editorInstance) {
      editorInstance.insertContent(selectedValue);
    }

    selectElement.selectedIndex = 0;
  }

  addResponseVariable(event: any) {
    const selectElement = event.target as HTMLSelectElement;
    const selectedValue = selectElement.value;
    const editorInstance = tinymce.get(this.stepEditorIds.Response);
    if (editorInstance) {
      editorInstance.insertContent(selectedValue);
    }

    selectElement.selectedIndex = 0;
  }

  get labelInvalidity() {
    const responseData = this.form.get('response_data') as FormGroup;
    return this.enableFormErrors && responseData.controls.label.status === FormControlStatus.Invalid;
  }

  get contentInvalidity() {
    const responseData = this.form.get('response_data') as FormGroup;
    return this.enableFormErrors && responseData.controls.content.status === FormControlStatus.Invalid;
  }

  getWidthControl(type: StepSectionWidths.Label | StepSectionWidths.Content): FormControl {
    const deviceKey = this.activeDevice.toString();
    const responseData = this.form.get('response_data') as FormGroup;
    return responseData.get('content_config').get(type).get(deviceKey) as FormControl;
  }

  isHidden(type: StepSectionWidths): boolean {
    const response_data = this.form.controls.response_data as FormGroup;
    return response_data.get('content_config').get(type).get('is_hidden').value;
  }

  onToggleChange(event: Event, type: StepSectionWidths) {
    const checked = (event.target as HTMLInputElement).checked;
    const response_data = this.form.controls.response_data as FormGroup;
    response_data.get('content_config').get(type).get('is_hidden').setValue(!checked);
  }

  loadWidgetVariables() {
    this.widgetSubscription = this.widgetService
      .getProductWidgetVariables()
      .subscribe((widgets: WidgetVariable[]) => {
        this.funnelPathVariables = buildPathVariables(widgets);
      });
  }

  ngOnDestroy() {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
    if (this.widgetSubscription) {
      this.widgetSubscription.unsubscribe();
    }
  }
}
